import type { CanvasObject, ConnectorCanvasObject } from "@refinement-canvas/domain";
import { isObjectPlacementTool, usesCrosshairCursor, useCanvasStore } from "../store/canvas-store";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useConnectorDragStore, type LiveFrame } from "../store/connector-drag-store";
import {
  arrowHeadPoints,
  buildRoundedPath,
  computeConnectorRoute,
  routeBoundingBox,
  type ConnectorFrame,
} from "../../../lib/connector-geometry";
import {
  CONNECTOR_DEFAULT_STROKE,
  CONNECTOR_DEFAULT_STROKE_WIDTH,
} from "../../../lib/connector-style";

interface Props {
  readonly object: ConnectorCanvasObject;
}

const ARROW_SIZE = 12;
const CORNER_RADIUS = 10;
// 矢じり・線幅が SVG 枠でクリップされないよう、外接矩形の周囲へ確保する余白。
const SVG_PAD = 16;
// クリックしやすいよう、見た目の線より太い透明の当たり判定線を重ねる。
const HIT_STROKE_WIDTH = 14;

// コネクタ（接続矢印）オブジェクト。
// 端点 from / to の接続先オブジェクトの現在位置（ドラッグ中はライブ枠）から都度ルーティングし、
// 直交折れ線 + 矢じりを SVG で描画する。クリックで選択でき、他オブジェクトと同様に削除できる。
export function ConnectorObject({ object }: Props): JSX.Element | null {
  const activeTool = useCanvasStore((s) => s.tool);
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));
  const readOnly = useCanvasStore((s) => s.readOnly);
  const setSelectedObjectIds = useCanvasStore((s) => s.setSelectedObjectIds);

  // 端点オブジェクトを doc store から購読し、移動・リサイズの確定で再ルーティングする。
  const fromObj = useCanvasDocStore((s) => s.objects[object.from.objectId]);
  const toObj = useCanvasDocStore((s) => s.objects[object.to.objectId]);
  // ドラッグ / リサイズ中は確定前のライブ枠を優先し、リアルタイムに追従させる。
  const fromLive = useConnectorDragStore((s) => s.frames[object.from.objectId]);
  const toLive = useConnectorDragStore((s) => s.frames[object.to.objectId]);

  const fromFrame = resolveFrame(fromLive, fromObj);
  const toFrame = resolveFrame(toLive, toObj);
  // 接続先が削除された等で端点が解決できない場合は何も描画しない（dangling 対策）。
  if (!fromFrame || !toFrame) return null;

  const points = computeConnectorRoute(fromFrame, object.from.side, toFrame, object.to.side);
  const bbox = routeBoundingBox(points);
  const effectiveStroke = object.stroke ?? CONNECTOR_DEFAULT_STROKE;
  const effectiveStrokeWidth = object.strokeWidth ?? CONNECTOR_DEFAULT_STROKE_WIDTH;
  // 選択中も太いコネクタが細く見えないよう、実効幅より 1px 太く、従来の 3px を下回らない。
  const stroke = isSelected ? "var(--color-selection)" : effectiveStroke;
  const strokeWidth = isSelected ? Math.max(effectiveStrokeWidth + 1, 3) : effectiveStrokeWidth;
  const hitStrokeWidth = Math.max(HIT_STROKE_WIDTH, effectiveStrokeWidth + 8);
  // 太い線や矢じりが SVG 枠で切れないよう、描画線幅の半分を余白へ上乗せする。
  const svgPad = SVG_PAD + Math.ceil(strokeWidth / 2);
  const originX = bbox.x - svgPad;
  const originY = bbox.y - svgPad;
  const width = bbox.w + svgPad * 2;
  const height = bbox.h + svgPad * 2;
  // ルート点を SVG ローカル座標へ変換する。
  const local = points.map((p) => ({ x: p.x - originX, y: p.y - originY }));
  const d = buildRoundedPath(local, CORNER_RADIUS);
  const tip = local[local.length - 1] ?? { x: 0, y: 0 };
  const beforeTip = local[local.length - 2] ?? tip;
  const head = arrowHeadPoints(tip, beforeTip, ARROW_SIZE);

  const onPointerDown = (event: React.PointerEvent<SVGPathElement>): void => {
    // 付箋 / 図形 / テキスト / スタンプ / コメント配置ツール選択中は、コネクタ上でも新規配置を許す
    // （イベントを止めず stage 側へバブリングさせる）。
    if (isObjectPlacementTool(activeTool)) {
      return;
    }
    event.stopPropagation();
    const selected = useCanvasStore.getState().selectedObjectIds;
    if (!selected.includes(object.id)) {
      setSelectedObjectIds([object.id]);
    }
  };

  return (
    <svg
      data-testid={`canvas-object-${object.id}`}
      data-object-type="connector"
      style={{
        position: "absolute",
        left: `${originX}px`,
        top: `${originY}px`,
        width: `${width}px`,
        height: `${height}px`,
        zIndex: object.zOrder,
        overflow: "visible",
        pointerEvents: "none",
      }}
    >
      {/* 当たり判定用の太い透明線（選択しやすくする）。 */}
      <path
        d={d}
        fill="none"
        stroke="transparent"
        strokeWidth={hitStrokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          pointerEvents: activeTool === "draw-pen" ? "none" : "stroke",
          // 参照専用（埋め込み）では default。pointer を出してよいのは、利用者が挙げたリンク・
          // リンクカード・コメントピンに限る（コネクタのクリックは選択ハイライトが出るだけで、
          // 閲覧者にとって意味のある遷移や操作は起きない）。配置ツール・ペン描画の選択中は、
          // コネクタ上でも新規作成（crosshair）であることを示す。
          cursor: readOnly ? "default" : usesCrosshairCursor(activeTool) ? "crosshair" : "pointer",
        }}
        onPointerDown={onPointerDown}
      />
      {/* 本体線。 */}
      <path
        d={d}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        pointerEvents="none"
      />
      {/* 矢じり（to 端へ向かう）。 */}
      {head && (
        <polyline
          points={`${head.left.x},${head.left.y} ${tip.x},${tip.y} ${head.right.x},${head.right.y}`}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          pointerEvents="none"
        />
      )}
    </svg>
  );
}

// ライブ枠（ドラッグ中の確定前フレーム）があればそれを、なければ doc store のオブジェクト枠を返す。
// 回転済み・回転中の図形にもアンカーを追従させるため、回転角も含めて返す。
function resolveFrame(
  live: LiveFrame | undefined,
  object: CanvasObject | undefined
): ConnectorFrame | null {
  if (live) return { x: live.x, y: live.y, w: live.w, h: live.h, rotation: live.rotation ?? 0 };
  if (object) {
    return { x: object.x, y: object.y, w: object.w, h: object.h, rotation: object.rotation ?? 0 };
  }
  return null;
}
